import {
    CheckSquareIcon,
    ClockIcon,
    BarChart3,
    BarChartIcon,
    LightbulbIcon,
    CheckCircleIcon,
    AlertTriangleIcon,
    ThumbsUpIcon,
    MessageSquareIcon,
} from "lucide-react";
import { Tooltip } from "@component/tooltip";
import {
    interviewStatusComplete,
    interviewStatusNone,
    interviewStatusPendingInterview,
    interviewStatusPendingVerification,
    interviewStatusInComplete,
    interviewStatusAborted,
    interviewStatusPartial,
} from "@constant/app";
import { CandidateInterviewStatus } from "@type/candidate";
import { InterviewInsight } from "@type/videoInterview";
import { getStatusBadgeClass } from "@util/string";
import { formatDuration, parseFormat } from "@util/date";

interface AiInterviewTabContentProps {
    interviewStatus: CandidateInterviewStatus | undefined;
    interviewInsights: InterviewInsight | null;
    isLoadingInterviewInsight: boolean;
    videoDuration: number | null;
}

export default function AiInterviewTabContent({
    interviewStatus,
    interviewInsights,
    isLoadingInterviewInsight,
    videoDuration,
}: AiInterviewTabContentProps) {
    return (
        <div className="flex flex-col space-y-6 mb-6">
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                        <CheckSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                        Interview Status
                    </h4>
                    {interviewStatus?.status === interviewStatusComplete ? (
                        <span className={`px-2 py-1 ${getStatusBadgeClass(interviewStatus?.status || interviewStatusPendingInterview)} rounded-full text-xs font-medium`}>
                            Completed
                        </span>
                    ) : (
                        <Tooltip
                            content={interviewStatus?.status === interviewStatusAborted ? "Candidate completed less than 20% of the interview." : "Candidate completed less than 50% of the interview."}
                        >
                            <span className={`px-2 py-1 ${getStatusBadgeClass(interviewStatus?.status || interviewStatusPendingInterview)} rounded-full text-xs font-medium`}>
                                {interviewStatus?.status === interviewStatusInComplete ? "Incomplete" : (interviewStatus?.status === interviewStatusPendingInterview || interviewStatus?.status === interviewStatusPendingVerification) ? "Pending" : (interviewStatus?.status || "Pending")}
                            </span>
                        </Tooltip>
                    )}
                </div>
                {interviewStatus?.status === interviewStatusComplete ?
                    (
                        <>
                            <p className="text-sm mt-3 text-gray-600 dark:text-gray-400 mb-2">
                                The candidate has completed their AI Interview on{" "}
                                {parseFormat(interviewStatus.updated_at || "", "h.mmA [on] D MMMM YYYY")}.
                            </p>
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                Duration: {videoDuration ? formatDuration(videoDuration) : <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-blue-600 ml-1" />
                                </div>}
                            </div>
                        </>
                    ) : (interviewStatus?.status === interviewStatusNone || interviewStatus?.status === interviewStatusPendingVerification) ? (
                        <p className="text-sm mt-3 text-gray-600">
                            The candidate is in the process of retaking their AI Interview.
                        </p>
                    ) : ((interviewStatus?.status === interviewStatusPendingInterview && !interviewStatus?.reason) || !interviewStatus?.status) ? (
                        // Absent/blank status = candidate has no submission → same copy as
                        // Pending Interview (#3131); no unknown-status blank state.
                        <p className="text-sm mt-3 text-gray-600">
                            The candidate has not started their AI Interview.
                        </p>
                    ) : (interviewStatus?.status === interviewStatusInComplete) ? (
                        <p className="text-sm mt-3 text-gray-600">
                            The candidate attempted the AI Interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but did not complete.
                        </p>
                    ) : (interviewStatus?.status === interviewStatusAborted) ? (
                        <p className="text-sm mt-3 text-gray-600">
                            The candidate attempted the AI interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but aborted.
                        </p>
                    ) : (interviewStatus?.status === interviewStatusPartial) && (
                        <p className="text-sm mt-3 text-gray-600">
                            The candidate attempted the AI interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but partial.
                        </p>
                    )}
            </div>
            {/* Insights section: hidden for Pending Interview / absent status (#3131),
                and also when there's genuinely nothing to render (e.g. a candidate
                retaking, with no insights loaded) — otherwise an empty space-y-6 div
                adds a stray gap before the recording player (#gap-fix). */}
            {(!!interviewStatus?.status && interviewStatus.status !== interviewStatusPendingInterview && (isLoadingInterviewInsight || !!interviewInsights)) && (
                <div className="space-y-6">
                    {!isLoadingInterviewInsight && interviewInsights?.summary && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                <BarChartIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                Interview Summary
                            </h4>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                {interviewInsights.summary}
                            </p>
                        </div>
                    )}
                    {(interviewInsights as InterviewInsight)?.interview_score && (interviewInsights as InterviewInsight).interview_score!.length > 0 && (
                        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <h4 className="font-medium text-base flex items-center">
                                        <BarChart3 className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                        Interview Scoring Breakdown
                                    </h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Based on weighted evaluation across {(interviewInsights as InterviewInsight).interview_score!.length}{" "}
                                        competencies.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Total Interview Score:</span>
                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-base font-semibold">
                                        {(interviewInsights as InterviewInsight).total_interview_score ?? 0}/100
                                    </span>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="space-y-4">
                                    {(interviewInsights as InterviewInsight).interview_score!.map((scoreItem, index) => (
                                        <div key={index} className="space-y-1.5">
                                            <div className="flex items-center justify-start gap-2">
                                                <span className="text-sm font-medium">{scoreItem.title}</span>
                                                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                    {scoreItem.score}/{scoreItem.max_score}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {scoreItem.explanation}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {isLoadingInterviewInsight && (
                        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                            <h4 className="font-medium text-base flex items-center mb-4">
                                <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                AI Insights & Recommendations
                            </h4>
                            <div className="flex items-center justify-center h-32">
                                <svg
                                    className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" stroke="currentColor"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4.93 4.93a10 10 0 0114.14 14.14L12 12z"></path>
                                </svg>
                            </div>
                        </div>
                    )}

                    {!isLoadingInterviewInsight && interviewInsights && (
                        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                            <h4 className="font-medium text-base flex items-center mb-4">
                                <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                AI Insights & Recommendations
                            </h4>
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <div className="flex items-center">
                                        <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                                        <span className="font-medium text-sm text-green-800 dark:text-green-200">
                                            Strengths
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {interviewInsights.strengths}
                                    </p>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex items-center">
                                        <AlertTriangleIcon className="h-4 w-4 mr-2 text-orange-600 dark:text-orange-400" />
                                        <span className="font-medium text-sm text-orange-800 dark:text-orange-200">
                                            Areas for Growth
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {interviewInsights.areas_for_growth}
                                    </p>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex items-center">
                                        <ThumbsUpIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                        <span className="font-medium text-sm text-blue-800 dark:text-blue-200">
                                            Overall Recommendation
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {interviewInsights.recommendation}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {interviewStatus?.status === interviewStatusComplete && !isLoadingInterviewInsight && interviewInsights?.key_questions_and_answers.length ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                        <MessageSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                        Key Questions & Responses
                    </h4>
                    <div className="space-y-4">
                        {interviewInsights?.key_questions_and_answers.map((elem, idx) => (
                            <div key={idx}>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                                    Q: {elem.question}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                                    {elem.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
